import mixpanel from "mixpanel-browser";
import { captureException } from "@sentry/vue";
import type User from "../datamodels/user";
import { MIXPANEL_TOKEN } from "@/shared/utils/env";

export const EVENT_NAMES = {
  Exercises: {
    ExerciseCreated: "ExerciseCreated",
    ExerciseDeleted: "ExerciseDeleted",
  },
  Announcements: {
    AnnouncementCreated: "AnnouncementCreated",
    AnnouncementPublished: "AnnouncementPublished",
    AnnouncementDraftSaved: "AnnouncementDraftSaved",
    AnnouncementDeleted: "AnnouncementDeleted",
    AnnouncementEdited: "AnnouncementEdited",
    AnnouncementSearched: "AnnouncementSearched",
    AnnouncementViewed: "AnnouncementViewed",
  },
  MEMBERSHIPS: {
    MembershipCanceled: "MembershipCanceled",
    MembershipParticipantChanged: "MembershipParticipantChanged",
  },
  Navigation: {
    NavAdminChat: "NavAdminChat",
    NavAdminEmail: "NavAdminEmail",
    NavAdminAnnouncements: "NavAdminAnnouncements",
    NavAdminTraining: "NavAdminTraining",
    NavAdminSubmissions: "NavAdminSubmissions",
    NavAdminWorkouts: "NavAdminWorkouts",
    NavAdminExercises: "NavAdminExercises",
    NavAdminUsers: "NavAdminUsers",
    NavAdminTeams: "NavAdminTeams",
    NavAdminTeam: "NavAdminTeam",
    NavAdminUser: "NavAdminUser",
    NavAdminWorkout: "NavAdminWorkout",
    NavAdminExercise: "NavAdminExercise",
    NavAdminVideoSubmission: "NavAdminVideoSubmission",
    NavAdminTrainingCollection: "NavAdminTrainingCollection",
    NavAdminStats: "NavAdminStats",
    NavAdminRegistrations: "NavAdminRegistrations",
    NavAdminMemberships: "NavAdminMemberships",
    NavAdminBookings: "NavAdminBookings",
    NavAdminSchedule: "NavAdminSchedule",
    NavAdminBookedSessions: "NavAdminBookedSessions",
    NavAdminNetworkManagement: "NavAdminNetworkManagement",
    NavAdminQuickLinks: "NavAdminQuickLinks",
    NavAdminOpenPortal: "NavAdminOpenPortal",
    NavAdminWebsite: "NavAdminWebsite",
    NavAdminSupport: "NavAdminSupport",
  },
  OFFERINGS: {
    OfferingCreated: "OfferingCreated",
    OfferingEdited: "OfferingEdited",
    OpenEnrollment: "OfferingEnrollmentOpened",
    CloseEnrollment: "OfferingEnrollmentClosed",
    ParticipantEnrolled: "ParticipantEnrolled",
    OfferingWizardStarted: "OfferingWizardStarted",
    OfferingWizardStepCompleted: "OfferingWizardStepCompleted",
    OfferingWizardExited: "OfferingWizardExited",
    OfferingPublishBlocked: "OfferingPublishBlocked",
  },
  GROUPS: {
    GroupCreated: "GroupCreated",
    GroupEdited: "GroupEdited",
    GroupDeleted: "GroupDeleted",
    GroupOfferingsChanged: "GroupOfferingsChanged",
    GroupsReordered: "GroupsReordered",
    GroupOfferingsReordered: "GroupOfferingsReordered",
    GroupLinkShared: "GroupLinkShared",
    GroupDiscountTiersSaved: "GroupDiscountTiersSaved",
  },
  ORGANIZATION_SETTINGS: {
    UpdateOrganizationSettings: "UpdateOrganizationSettings",
    LinkAdded: "LinkAdded",
    LinkUpdated: "LinkUpdated",
    LinkDeleted: "LinkDeleted",
  },
  PASSWORD: {
    PasswordResetRequested: "PasswordResetRequested",
    PasswordResetFailed: "PasswordResetFailed",
    PasswordResetSucceeded: "PasswordResetSucceeded",
  },
  PAYMENTS: {
    InvoiceCreated: "InvoiceCreated",
    ScheduledPaymentAmountChanged: "ScheduledPaymentAmountChanged",
    ScheduledPaymentWaived: "ScheduledPaymentWaived",
  },
  REGISTRANTS: {
    RegistrantCanceled: "RegistrantCanceled",
    RegistrantRefunded: "RegistrantRefunded",
    RegistrantParticipantChanged: "RegistrantParticipantChanged",
  },
  STATS: {
    StatCategoryCreated: "StatCategoryCreated",
    StatCategoryUpdated: "StatCategoryUpdated",
  },
  TEAMS: {
    TEAM_CREATED: "TeamCreated",
    TEAM_DELETED: "TeamDeleted",
  },
  TrainingCollections: {
    TrainingCollectionCreated: "TrainingCollectionCreated",
    TrainingDrillCreated: "TrainingDrillCreated",
    TrainingComparisonVideoAdded: "TrainingComparisonVideoAdded",
    TrainingDrillExerciseAdded: "TrainingDrillExerciseAdded",
    TrainingDrillDeleted: "TrainingDrillDeleted",
  },
  USER: {
    UserCreated: "UserCreated",
    UserAddedToOrganization: "UserAddedToOrganization",
    UserMadeCoach: "UserMadeCoach",
    UserMadeAdmin: "UserMadeAdmin",
    AdminNavUser: "AdminNavUser",
    AdminLoggedIn: "AdminLoggedIn",
    UserEmailsCopied: "UserEmailsCopied",
  },
  VideoSubmissions: {
    SubmissionCoachChanged: "SubmissionCoachChanged",
    SubmissionVideoViewed: "SubmissionVideoViewed",
    SubmissionsRefreshed: "SubmissionsRefreshed",
    VideoSubmittedFromAdmin: "VideoSubmittedFromAdmin",
  },
  WEBSITE: {
    WebsiteOpenCms: "WebsiteOpenCms",
    WebsiteOpenDrive: "WebsiteOpenDrive",
    // props: { site_content: boolean, site_images: boolean }
    WebsiteUpdateRequested: "WebsiteUpdateRequested",
  },
  Workouts: {
    CalendarSelected: "CalendarSelected",
    CalendarCreated: "CalendarCreated",
    WorkoutCreated: "WorkoutCreated",
    WorkoutEdited: "WorkoutEdited",
    WorkoutDeleted: "WorkoutDeleted",
    WorkoutPasted: "WorkoutPasted",
    SetAdded: "SetAdded",
    SupersetAdded: "SupersetAdded",
    ExerciseBlockAdded: "ExerciseBlockAdded",
    CalendarAssigned: "CalendarAssigned",
  },
} as const;

export const addMixpanelEvent = (
  eventName: string,
  tags: Record<string, unknown> = {}
) => {
  try {
    mixpanel.track(eventName, tags);
  } catch (e: any) {
    captureException(e);
  }
};

export const mixpanelLogout = () => {
  if (MIXPANEL_TOKEN) {
    mixpanel.reset();
  }
};

type MixpanelOrganization = { id?: number; name?: string };

/** Stamps every later event with the org the admin is currently working in. */
export const setMixpanelOrganization = (
  organization: MixpanelOrganization | null | undefined
) => {
  if (!MIXPANEL_TOKEN || !organization?.id) return;
  try {
    mixpanel.register({ orgId: organization.id, orgName: organization.name });
  } catch (e: any) {
    captureException(e);
  }
};

export const mixpanelLogin = (
  user: User,
  organization: MixpanelOrganization | null | undefined
) => {
  if (MIXPANEL_TOKEN) {
    mixpanel.identify(user?.id.toString());

    // ios sets team, and teamId but not including as an Admin might be part of multiple teams
    mixpanel.people.set({
      $email: user?.email,
      role: user?.is_coach ? "Coach" : "Athlete",
      loginType: "Email",
      name: user?.name,
      userEmail: user?.email,
      userId: user?.id,
      orgId: organization?.id,
      orgName: organization?.name,
    });
    setMixpanelOrganization(organization);
  }
};
